import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { TrendingUp, TrendingDown } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const symptomConfig = {
  hotFlashes: { label: 'Hot Flashes', color: '#DC2626', pillar: 'Body' },
  weightChanges: { label: 'Weight', color: '#EA580C', pillar: 'Body' },
  skinHair: { label: 'Skin & Hair', color: '#D97706', pillar: 'Body' },
  moodSwings: { label: 'Mood', color: '#7C2D12', pillar: 'Mind' },
  brainFog: { label: 'Mental Clarity', color: '#92400E', pillar: 'Mind' },
  anxiety: { label: 'Anxiety', color: '#78350F', pillar: 'Mind' },
  sleepQuality: { label: 'Sleep Quality', color: '#134E4A', pillar: 'Vitality' },
  fatigue: { label: 'Energy', color: '#065F46', pillar: 'Vitality' },
  libido: { label: 'Libido', color: '#047857', pillar: 'Vitality' }
};

const ProgressDashboard = ({ user, refreshTrigger }) => {
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState(30);
  const [chartData, setChartData] = useState([]);
  const [insights, setInsights] = useState([]);
  const [visibleSymptoms, setVisibleSymptoms] = useState(
    Object.keys(symptomConfig).reduce((acc, key) => ({ ...acc, [key]: true }), {})
  );

  useEffect(() => {
    if (user?.id) {
      fetchProgressData();
    }
  }, [user?.id, selectedPeriod, refreshTrigger]);

  const fetchProgressData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/users/${user.id}/wellness-tracker/history/${selectedPeriod}`);
      
      if (response.data.success && response.data.data.length > 0) {
        // Transform data for the chart
        const transformed = response.data.data.map(entry => {
          // Parse date without timezone conversion (YYYY-MM-DD format)
          const [year, month, day] = entry.date.split('-').map(Number);
          const localDate = new Date(year, month - 1, day);
          
          return {
            date: localDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
            fullDate: entry.date,
            ...entry.data
          };
        });
        
        setChartData(transformed);
        calculateInsights(transformed);
      } else {
        setChartData([]);
        setInsights([]);
      }
    } catch (error) {
      console.error('Error fetching progress data:', error);
      setChartData([]);
      setInsights([]);
    } finally {
      setLoading(false);
    }
  };

  const calculateInsights = (data) => {
    if (data.length < 2) {
      setInsights([]);
      return;
    }

    const foundInsights = [];
    
    // Calculate for last 30 days of data (or available data if less)
    const recentData = data.slice(-Math.min(30, data.length));
    const firstHalf = recentData.slice(0, Math.floor(recentData.length / 2));
    const secondHalf = recentData.slice(Math.floor(recentData.length / 2));

    Object.keys(symptomConfig).forEach(symptom => {
      // Calculate averages for each half
      const firstAvg = firstHalf.reduce((sum, d) => sum + (d[symptom] || 0), 0) / firstHalf.length;
      const secondAvg = secondHalf.reduce((sum, d) => sum + (d[symptom] || 0), 0) / secondHalf.length;
      
      const percentChange = ((secondAvg - firstAvg) / firstAvg) * 100;

      // Highlight symptoms with >25% change
      if (Math.abs(percentChange) > 25) {
        foundInsights.push({
          symptom: symptomConfig[symptom].label,
          change: percentChange,
          improved: percentChange > 0,
          color: symptomConfig[symptom].color
        });
      }
    });

    // Sort by absolute change
    foundInsights.sort((a, b) => Math.abs(b.change) - Math.abs(a.change));
    setInsights(foundInsights);
  };

  if (loading) {
    return (
      <div className="mt-6 bg-burgundy/5 rounded-lg p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-burgundy/20 rounded w-1/3"></div>
          <div className="h-64 bg-burgundy/10 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-6 bg-burgundy/5 rounded-lg p-6">
      <div className="mb-6">
        <h4 className="text-xl font-semibold text-burgundy mb-2 font-serif">Progress Tracking</h4>
        <p className="text-sm text-burgundy/70">Visualize your wellness journey over time</p>
      </div>

      {/* Time Period Filters */}
      <div className="flex flex-wrap gap-2 mb-6">
        {[15, 30, 45, 60, 75, 90].map(days => (
          <Button
            key={days}
            onClick={() => setSelectedPeriod(days)}
            variant={selectedPeriod === days ? 'default' : 'outline'}
            className={selectedPeriod === days 
              ? 'bg-burgundy hover:bg-burgundy/80 text-white' 
              : 'border-burgundy text-burgundy hover:bg-burgundy hover:text-white'
            }
            size="sm"
          >
            {days} Days
          </Button>
        ))}
      </div>

      {/* Insights Section */}
      {insights.length > 0 && (
        <div className="mb-6 bg-white rounded-lg p-4 border border-burgundy/20">
          <h5 className="font-semibold text-burgundy mb-3 flex items-center">
            <TrendingUp className="h-5 w-5 mr-2" />
            Key Insights (Last 30 Days)
          </h5>
          <div className="flex flex-wrap gap-2">
            {insights.map((insight, idx) => (
              <Badge
                key={idx}
                variant="outline"
                className="text-sm py-1.5 px-3"
                style={{ 
                  borderColor: insight.color,
                  color: insight.color,
                  backgroundColor: `${insight.color}10`
                }}
              >
                {insight.improved ? <TrendingUp className="h-3 w-3 inline mr-1" /> : <TrendingDown className="h-3 w-3 inline mr-1" />}
                {insight.symptom}: {insight.improved ? '+' : ''}{insight.change.toFixed(0)}%
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* Chart */}
      {chartData.length > 0 ? (
        <div className="bg-white rounded-lg p-4 border border-burgundy/20">
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={chartData} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#7C2D1210" />
              <XAxis 
                dataKey="date" 
                stroke="#7C2D12"
                style={{ fontSize: '12px' }}
              />
              <YAxis 
                domain={[1, 10]}
                stroke="#7C2D12"
                style={{ fontSize: '12px' }}
              />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#FFF8F0', 
                  border: '1px solid #7C2D1230',
                  borderRadius: '8px'
                }}
              />
              <Legend 
                wrapperStyle={{ fontSize: '12px' }}
                iconType="line"
              />
              
              {Object.entries(symptomConfig).map(([key, config]) => (
                visibleSymptoms[key] && (
                  <Line
                    key={key}
                    type="monotone"
                    dataKey={key}
                    name={config.label}
                    stroke={config.color}
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    activeDot={{ r: 5 }}
                  />
                )
              ))}
            </LineChart>
          </ResponsiveContainer>
          
          {/* Legend by Pillar - Click to Toggle */}
          <div className="mt-4">
            <p className="text-sm text-burgundy/70 mb-3 text-center">Click symptoms to turn them off and on to customize your chart</p>
            <div className="grid grid-cols-3 gap-4 text-xs">
              <div>
                <p className="font-semibold text-burgundy mb-2">Radiant Body</p>
              {Object.entries(symptomConfig)
                .filter(([, config]) => config.pillar === 'Body')
                .map(([key, config]) => (
                  <div 
                    key={key} 
                    className="flex items-center gap-2 mb-1 cursor-pointer hover:opacity-70 transition-opacity"
                    onClick={() => setVisibleSymptoms(prev => ({ ...prev, [key]: !prev[key] }))}
                  >
                    <div 
                      className="w-3 h-0.5" 
                      style={{ 
                        backgroundColor: visibleSymptoms[key] ? config.color : '#ccc',
                        opacity: visibleSymptoms[key] ? 1 : 0.3
                      }}
                    ></div>
                    <span 
                      style={{ 
                        color: visibleSymptoms[key] ? config.color : '#999',
                        textDecoration: visibleSymptoms[key] ? 'none' : 'line-through'
                      }}
                    >
                      {config.label}
                    </span>
                  </div>
                ))}
              </div>
              <div>
                <p className="font-semibold text-burgundy mb-2">Balanced Mind</p>
              {Object.entries(symptomConfig)
                .filter(([, config]) => config.pillar === 'Mind')
                .map(([key, config]) => (
                  <div 
                    key={key} 
                    className="flex items-center gap-2 mb-1 cursor-pointer hover:opacity-70 transition-opacity"
                    onClick={() => setVisibleSymptoms(prev => ({ ...prev, [key]: !prev[key] }))}
                  >
                    <div 
                      className="w-3 h-0.5" 
                      style={{ 
                        backgroundColor: visibleSymptoms[key] ? config.color : '#ccc',
                        opacity: visibleSymptoms[key] ? 1 : 0.3
                      }}
                    ></div>
                    <span 
                      style={{ 
                        color: visibleSymptoms[key] ? config.color : '#999',
                        textDecoration: visibleSymptoms[key] ? 'none' : 'line-through'
                      }}
                    >
                      {config.label}
                    </span>
                  </div>
                ))}
              </div>
              <div>
                <p className="font-semibold text-burgundy mb-2">Enduring Vitality</p>
              {Object.entries(symptomConfig)
                .filter(([, config]) => config.pillar === 'Vitality')
                .map(([key, config]) => (
                  <div 
                    key={key} 
                    className="flex items-center gap-2 mb-1 cursor-pointer hover:opacity-70 transition-opacity"
                    onClick={() => setVisibleSymptoms(prev => ({ ...prev, [key]: !prev[key] }))}
                  >
                    <div 
                      className="w-3 h-0.5" 
                      style={{ 
                        backgroundColor: visibleSymptoms[key] ? config.color : '#ccc',
                        opacity: visibleSymptoms[key] ? 1 : 0.3
                      }}
                    ></div>
                    <span 
                      style={{ 
                        color: visibleSymptoms[key] ? config.color : '#999',
                        textDecoration: visibleSymptoms[key] ? 'none' : 'line-through'
                      }}
                    >
                      {config.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="text-center py-12 bg-white rounded-lg border border-burgundy/20">
          <p className="text-burgundy/60 mb-2">No tracking data found for this period.</p>
          <p className="text-xs text-burgundy/50">Start tracking daily to visualize your wellness journey!</p>
        </div>
      )}
    </div>
  );
};

export default ProgressDashboard;